import type { Report } from '@reports/domain/entities';
import { createReportId } from '@reports/domain/entities';
import {
  ReportTypes,
  ReportInputTypes,
  ReportBlockTypes,
  ReportDataFormats,
  ReportChartTypes,
  ExportFormats,
} from '@reports/domain/types';
import {
  createTableBlockConfig,
  createChartBlockConfig,
  createReportFilterConfig,
  createExportConfig,
} from '@reports/domain/value-objects';

import { REPORT_ENDPOINTS } from '@reports/application/configs/endpoints';
import { REPORT_FILTERS_KEYS } from '../reportKeysConst';
import { REPORT_ACADEMY_LINKS } from '../reportAcademyConst';

export const productsReportDefinition: Report = {
  id: createReportId('products-report'),
  type: ReportTypes.PRODUCTS,
  metadata: {
    name: 'reportProducts:name',
    description: 'reportProducts:description',
    tooltip: 'reportProducts:tooltip',
    parrotAcademyLink: REPORT_ACADEMY_LINKS.PRODUCTS,
    filterDateRangeInfo: 'reportProducts:filters.dateRangeInfo',
  },
  filters: [
    createReportFilterConfig({
      key: REPORT_FILTERS_KEYS.PROVIDER,
      label: 'reportCommon:filterKeys.provider.label',
      inputType: ReportInputTypes.MULTISELECT,
      placeholder: 'reportCommon:filterKeys.provider.placeholder',
    }),
    createReportFilterConfig({
      key: REPORT_FILTERS_KEYS.ORDER_TYPE,
      label: 'reportCommon:filterKeys.orderType.label',
      inputType: ReportInputTypes.MULTISELECT,
      placeholder: 'reportCommon:filterKeys.orderType.placeholder',
    }),
    createReportFilterConfig({
      key: REPORT_FILTERS_KEYS.BRAND,
      label: 'reportCommon:filterKeys.brand.label',
      inputType: ReportInputTypes.MULTISELECT,
      placeholder: 'reportCommon:filterKeys.brand.placeholder',
    }),
  ],
  sections: [
    {
      key: 'chart',
      title: 'reportCommon:sections.charts.title',
      blocks: [
        createChartBlockConfig({
          key: 'topVentaArticulos',
          title: 'reportProducts:blocks.charts.topVentaArticulos.title',
          endpoint: `${REPORT_ENDPOINTS.CHARTS}/18`,
          type: ReportBlockTypes.CHART,
          chartType: ReportChartTypes.BAR_HORIZONTAL,
          width: 'sm',
          seriesConfig: [
            {
              key: 'total_sales_amount',
              label: 'reportProducts:blocks.charts.topVentaArticulos.series.label',
              format: {
                type: ReportDataFormats.CURRENCY,
                options: {
                  decimals: 2,
                  prefix: '$',
                },
              },
            },
          ],
          options: {
            colors: ['#EB6668'],
            showLegend: true,
            showLabels: true,
            showTooltip: true,
            showTopBarLabels: true,
          },
        }),
        createChartBlockConfig({
          key: 'ventaPorCategoria',
          title: 'reportProducts:blocks.charts.ventaPorCategoria.title',
          endpoint: `${REPORT_ENDPOINTS.CHARTS}/19`,
          type: ReportBlockTypes.CHART,
          chartType: ReportChartTypes.PIE,
          width: 'sm',
          seriesConfig: [
            {
              key: 'total_sales_amount',
              label: 'reportProducts:blocks.charts.ventaPorCategoria.series.label',
              format: {
                type: ReportDataFormats.CURRENCY,
                options: {
                  decimals: 2,
                  prefix: '$',
                },
              },
            },
          ],
          options: {
            colors: ['#EB6668'],
            showLegend: true,
            showLabels: true,
            showTooltip: true,
            showTopBarLabels: false,
            groupTopValues: true,
          },
        }),
        createChartBlockConfig({
          key: 'ventaPorTipoArticulo',
          title: 'reportProducts:blocks.charts.ventaPorTipoArticulo.title',
          endpoint: `${REPORT_ENDPOINTS.CHARTS}/20`,
          type: ReportBlockTypes.CHART,
          chartType: ReportChartTypes.PIE,
          width: 'sm',
          seriesConfig: [
            {
              key: 'total_sales_amount',
              label: 'reportProducts:blocks.charts.ventaPorTipoArticulo.series.label',
              format: {
                type: ReportDataFormats.CURRENCY,
                options: {
                  decimals: 2,
                  prefix: '$',
                },
              },
            },
          ],
          options: {
            colors: ['#EB6668'],
            showLegend: true,
            showLabels: true,
            showTooltip: true,
            showTopBarLabels: false,
            groupTopValues: true,
          },
        }),
        createChartBlockConfig({
          key: 'ventaPorMarca',
          title: 'reportProducts:blocks.charts.ventaPorMarca.title',
          endpoint: `${REPORT_ENDPOINTS.CHARTS}/21`,
          type: ReportBlockTypes.CHART,
          chartType: ReportChartTypes.PIE,
          width: 'md',
          seriesConfig: [
            {
              key: 'total_sales_amount',
              label: 'reportProducts:blocks.charts.ventaPorMarca.series.label',
              format: {
                type: ReportDataFormats.CURRENCY,
                options: {
                  decimals: 2,
                  prefix: '$',
                },
              },
            },
          ],
          options: {
            colors: ['#EB6668'],
            showLegend: true,
            showLabels: true,
            showTooltip: true,
            showTopBarLabels: false,
            groupTopValues: true,
          },
        }),
        createChartBlockConfig({
          key: 'ventaPorTipoOrden',
          title: 'reportProducts:blocks.charts.ventaPorTipoOrden.title',
          endpoint: `${REPORT_ENDPOINTS.CHARTS}/22`,
          type: ReportBlockTypes.CHART,
          chartType: ReportChartTypes.BAR,
          width: 'md',
          seriesConfig: [
            {
              key: 'total_sales_amount',
              label: 'reportProducts:blocks.charts.ventaPorTipoOrden.series.label',
              format: {
                type: ReportDataFormats.CURRENCY,
                options: {
                  decimals: 2,
                  prefix: '$',
                },
              },
            },
          ],
          options: {
            colors: ['#EB6668'],
            showLegend: true,
            showLabels: false,
            showTooltip: true,
            showTopBarLabels: false,
          },
        }),
      ],
    },
    {
      key: 'summary',
      title: 'reportCommon:sections.summary.title',
      blocks: [
        createTableBlockConfig({
          key: 'productsSummaryTable',
          endpoint: REPORT_ENDPOINTS.PRODUCTS.SUMMARY,
          type: ReportBlockTypes.TABLE,
          columnsConfig: [
            {
              key: 'name',
              label: 'reportProducts:blocks.summaryTable.columns.name.label',
              format: {
                type: ReportDataFormats.TEXT,
              },
              align: 'left',
              bold: false,
            },
            {
              key: 'category_uuid',
              label: 'reportProducts:blocks.summaryTable.columns.categoryUuid.label',
              format: {
                type: ReportDataFormats.TEXT,
              },
              align: 'left',
            },
            {
              key: 'product_type_uuid',
              label: 'reportProducts:blocks.summaryTable.columns.productTypeUuid.label',
              format: {
                type: ReportDataFormats.TEXT,
              },
              align: 'left',
            },
            {
              key: 'quantity_sum',
              label: 'reportProducts:blocks.summaryTable.columns.quantitySum.label',
              format: {
                type: ReportDataFormats.NUMBER,
                options: {
                  decimals: 2,
                },
              },
              align: 'right',
            },
            {
              key: 'price_avg_amount',
              label: 'reportProducts:blocks.summaryTable.columns.priceAvgAmount.label',
              format: {
                type: ReportDataFormats.CURRENCY,
                options: {
                  decimals: 2,
                  prefix: '$',
                },
              },
              align: 'right',
            },
            {
              key: 'gross_sales_amount',
              label: 'reportProducts:blocks.summaryTable.columns.grossSalesAmount.label',
              format: {
                type: ReportDataFormats.CURRENCY,
                options: {
                  decimals: 2,
                  prefix: '$',
                },
              },
              align: 'right',
            },
            {
              key: 'discounts_amount',
              label: 'reportProducts:blocks.summaryTable.columns.discountsAmount.label',
              format: {
                type: ReportDataFormats.CURRENCY,
                options: {
                  decimals: 2,
                  prefix: '$',
                },
              },
              align: 'right',
            },
            {
              key: 'total_sales_amount',
              label: 'reportProducts:blocks.summaryTable.columns.totalSalesAmount.label',
              format: {
                type: ReportDataFormats.CURRENCY,
                options: {
                  decimals: 2,
                  prefix: '$',
                },
              },
              align: 'right',
            },
          ],
          columnToggleEnabled: false,
          paginationEnabled: true,
          pageSize: 50,
        }),
      ],
    },
    {
      key: 'details',
      title: 'reportCommon:sections.details.title',
      blocks: [
        createTableBlockConfig({
          key: 'productsDetailsTable',
          endpoint: REPORT_ENDPOINTS.PRODUCTS.DETAILS,
          type: ReportBlockTypes.TABLE,
          columnsConfig: [
            {
              key: 'sale_date',
              label: 'reportProducts:blocks.detailsTable.columns.saleDate.label',
              format: {
                type: ReportDataFormats.DATE,
              },
              align: 'left',
            },
            {
              key: 'sale_time',
              label: 'reportProducts:blocks.detailsTable.columns.saleTime.label',
              format: {
                type: ReportDataFormats.TEXT,
              },
              align: 'right',
            },
            {
              key: 'channel',
              label: 'reportProducts:blocks.detailsTable.columns.channel.label',
              format: {
                type: ReportDataFormats.TEXT,
              },
              align: 'left',
            },
            {
              key: 'brand_name',
              label: 'reportProducts:blocks.detailsTable.columns.brandName.label',
              format: {
                type: ReportDataFormats.TEXT,
              },
              align: 'left',
            },
            {
              key: 'order_type_name',
              label: 'reportProducts:blocks.detailsTable.columns.orderTypeName.label',
              format: {
                type: ReportDataFormats.TEXT,
              },
              align: 'left',
            },
            {
              key: 'order_reference',
              label: 'reportProducts:blocks.detailsTable.columns.orderReference.label',
              format: {
                type: ReportDataFormats.TEXT,
              },
              align: 'left',
            },
            {
              key: 'sku',
              label: 'reportProducts:blocks.detailsTable.columns.sku.label',
              format: {
                type: ReportDataFormats.TEXT,
              },
              align: 'left',
            },
            {
              key: 'item_name',
              label: 'reportProducts:blocks.detailsTable.columns.itemName.label',
              format: {
                type: ReportDataFormats.TEXT,
              },
              align: 'left',
            },
            {
              key: 'category_name',
              label: 'reportProducts:blocks.detailsTable.columns.categoryName.label',
              format: {
                type: ReportDataFormats.TEXT,
              },
              align: 'left',
            },
            {
              key: 'product_type_name',
              label: 'reportProducts:blocks.detailsTable.columns.productTypeName.label',
              format: {
                type: ReportDataFormats.TEXT,
              },
              align: 'left',
            },
            {
              key: 'role',
              label: 'reportProducts:blocks.detailsTable.columns.role.label',
              format: {
                type: ReportDataFormats.TEXT,
              },
              align: 'left',
            },
            {
              key: 'quantity',
              label: 'reportProducts:blocks.detailsTable.columns.quantity.label',
              format: {
                type: ReportDataFormats.NUMBER,
                options: {
                  decimals: 2,
                },
              },
              align: 'right',
            },
            {
              key: 'unit_price',
              label: 'reportProducts:blocks.detailsTable.columns.unitPrice.label',
              format: {
                type: ReportDataFormats.CURRENCY,
                options: {
                  decimals: 2,
                  prefix: '$',
                },
              },
              align: 'right',
            },
            {
              key: 'gross_sales',
              label: 'reportProducts:blocks.detailsTable.columns.grossSales.label',
              format: {
                type: ReportDataFormats.CURRENCY,
                options: {
                  decimals: 2,
                  prefix: '$',
                },
              },
              align: 'right',
            },
            {
              key: 'item_discount',
              label: 'reportProducts:blocks.detailsTable.columns.itemDiscount.label',
              format: {
                type: ReportDataFormats.CURRENCY,
                options: {
                  decimals: 2,
                  prefix: '$',
                },
              },
              align: 'right',
            },
            {
              key: 'total_sales',
              label: 'reportProducts:blocks.detailsTable.columns.totalSales.label',
              format: {
                type: ReportDataFormats.CURRENCY,
                options: {
                  decimals: 2,
                  prefix: '$',
                },
              },
              align: 'right',
            },
            {
              key: 'status',
              label: 'reportProducts:blocks.detailsTable.columns.status.label',
              format: {
                type: ReportDataFormats.TEXT,
              },
              align: 'left',
            },
          ],
          columnToggleEnabled: true,
          paginationEnabled: true,
          pageSize: 50,
        }),
      ],
    },
  ],
  export: createExportConfig({
    name: 'reportProducts:export.name',
    endpoint: REPORT_ENDPOINTS.PRODUCTS.EXPORT,
    slugs: ['16', '17'],
    format: ExportFormats.EXCEL,
  }),
};
